// ** Amplify
import { API } from "aws-amplify"
import { adminsApiGet } from "@helpers/adminsApiClient"
import { handleApiAuthError } from "@helpers/authSession"
// **  store names
import { STORES } from "@constants/redux"

// ** main app Logger
import { appLogger } from "@helpers/logger"

// ** constants
import { globalConst } from "@constants/global"

// **  utility
import { isEmpty, drop ,omit} from "lodash"

// ** fuzzy search support
import Fuse from "fuse.js"

const shouldSkipProtectedDashboardCalls = () => {
  // Admin dashboard must be server-authoritative across browsers.
  return false
}

const V2_BACKEND_BASE = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_V2_BACKEND_URL ||
  ""
).replace(/\/+$/, "")
const hasV2Backend = () => V2_BACKEND_BASE.length > 0

const getV2Headers = () => {
  if (typeof window === "undefined") return { "Content-Type": "application/json" }
  const token = localStorage.getItem("accessToken")
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" }
}

const v2Request = async (path, options = {}) => {
  const response = await fetch(`${V2_BACKEND_BASE}${path}`, {
    method: options.method || "GET",
    headers: getV2Headers(),
    body: options.body ? JSON.stringify(options.body) : undefined
  })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || `v2 request failed (${response.status})`)
  }
  if (response.status === 204) return null
  return await response.json()
}

const v2RequestWithCommonsFallback = async (path, options = {}) => {
  try {
    return await v2Request(path, options)
  } catch (error) {
    if (process.env.NODE_ENV === "production") throw error
    const message = String(error?.message || error || "")
    if (!path.startsWith("/commons/") && message.includes("404")) {
      try {
        return await v2Request(`/commons${path}`, options)
      } catch (innerError) {
        const innerMessage = String(innerError?.message || innerError || "")
        if (innerMessage.includes("404")) {
          const local3010Base = "http://localhost:3010"
          const response = await fetch(`${local3010Base}/commons${path}`, {
            method: options.method || "GET",
            headers: getV2Headers(),
            body: options.body ? JSON.stringify(options.body) : undefined
          })
          if (!response.ok) {
            const text = await response.text()
            throw new Error(text || `v2 request failed (${response.status})`)
          }
          if (response.status === 204) return null
          return await response.json()
        }
        throw innerError
      }
    }
    throw error
  }
}

const getAdminsApiWithCommonsFallback = async (path) => {
  try {
    return await API.get("adminsApi", path, {})
  } catch (error) {
    const statusCode =
      error?.response?.status ||
      error?.response?.statusCode ||
      error?.status ||
      error?.statusCode
    if (statusCode === 404 && !path.startsWith("/commons/")) {
      return await API.get("adminsApi", `/commons${path}`, {})
    }
    throw error
  }
}

const getAdminsApiDirect = async (path) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`
  if (hasV2Backend()) {
    return await adminsApiGet(normalizedPath)
  }
  if (process.env.NODE_ENV !== "production" && shouldSkipProtectedDashboardCalls()) {
    const directBases = ["http://localhost:3010", "http://localhost:3002"]
    for (const base of directBases) {
      const normalized = base.replace(/\/+$/, "")
      const url = `${normalized}/commons${normalizedPath}`
      const result = await fetchJsonWithTimeout(url, 1200)
      if (result !== null) return result
    }
  }
  try {
    return await API.get("adminsApi", path, {})
  } catch (error) {
    if (process.env.NODE_ENV === "production" || !shouldSkipProtectedDashboardCalls()) throw error
    const directBases = ["http://localhost:3010", "http://localhost:3002"]
    for (const base of directBases) {
      const normalized = base.replace(/\/+$/, "")
      const url = `${normalized}/commons${normalizedPath}`
      const result = await fetchJsonWithTimeout(url, 1500)
      if (result !== null) return result
    }
    throw error
  }
}

const putAdminsApiDirect = async (path, body = {}) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`
  if (hasV2Backend()) {
    try {
      return await putJsonWithTimeout(`${V2_BACKEND_BASE}${normalizedPath}`, body, 2000)
    } catch (_v2Error) {
      // fall through to Amplify / dev commons
    }
  }
  if (process.env.NODE_ENV !== "production" && shouldSkipProtectedDashboardCalls()) {
    const directBases = ["http://localhost:3010", "http://localhost:3002"]
    for (const base of directBases) {
      const normalized = base.replace(/\/+$/, "")
      const url = `${normalized}/commons${normalizedPath}`
      try {
        return await putJsonWithTimeout(url, body, 2000)
      } catch (_innerError) {
        // try next base
      }
    }
  }
  try {
    return await API.put("adminsApi", path, { body })
  } catch (error) {
    if (process.env.NODE_ENV === "production" || !shouldSkipProtectedDashboardCalls()) throw error
    const directBases = ["http://localhost:3010", "http://localhost:3002"]
    for (const base of directBases) {
      const normalized = base.replace(/\/+$/, "")
      const url = `${normalized}/commons${normalizedPath}`
      try {
        return await putJsonWithTimeout(url, body, 2500)
      } catch (_innerError) {
        // try next base
      }
    }
    throw error
  }
}

const resolveItemParentKey = (row) => {
  const parent = row?.parent
  if (parent && typeof parent === "object") {
    return String(parent.pk || parent.id || parent._id || "")
  }
  return String(
    row?.parentId ||
      row?.parent_id ||
      row?.companyId ||
      row?.company_id ||
      parent ||
      ""
  )
}

const partnerTitleFromApiRow = (row) =>
  row?.title ||
  row?.name ||
  row?.company_name ||
  row?.companyName ||
  row?.email ||
  ""

const withTimeout = (promise, timeoutMs = 1200) => {
  return Promise.race([
    promise,
    new Promise((resolve) => {
      setTimeout(() => resolve(null), timeoutMs)
    })
  ])
}

const fetchJsonWithTimeout = async (url, timeoutMs = 1200) => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: getV2Headers(),
      signal: controller.signal
    })
    if (!response.ok) return null
    return await response.json()
  } catch (_error) {
    return null
  } finally {
    clearTimeout(timer)
  }
}

const putJsonWithTimeout = async (url, body = {}, timeoutMs = 2000) => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(url, {
      method: "PUT",
      headers: getV2Headers(),
      body: JSON.stringify(body || {}),
      signal: controller.signal
    })
    if (!response.ok) throw new Error(`put failed (${response.status})`)
    if (response.status === 204) return null
    return await response.json().catch(() => ({}))
  } finally {
    clearTimeout(timer)
  }
}

/** V2 Nest GET /realestate/v1/:id, then (local only) ECS adminsApi — covers stale Nest dist or Dynamo-only pks. */
const fetchLocalRealestatePartnerById = async (id) => {
  if (!id) return null
  if (shouldSkipProtectedDashboardCalls()) {
    try {
      return await getAdminsApiDirect(`/realestate/v1/${id}`)
    } catch (_e1) {
      try {
        return await getAdminsApiDirect(`/realestate/v1/${id}/`)
      } catch (_e2) {
        // continue to direct local service fallback below
      }
    }
  }
  if (shouldSkipProtectedDashboardCalls()) {
    // Direct local migration service fallback to avoid adminsApi/Dynamo timeout dependency.
    const directBases = ["http://localhost:3000", "http://localhost:3010"]
    for (const base of directBases) {
      const normalized = base.replace(/\/+$/, "")
      const direct = await fetchJsonWithTimeout(`${normalized}/realestate/v1/${id}`, 1000)
      if (direct) return direct
    }
  }
  if (hasV2Backend()) {
    try {
      return await v2Request(`/realestate/v1/${id}`)
    } catch (_v2) {
      return null
    }
  }
  return null
}

/** After listing items, resolve company names by id so rows missing from /realestate/v1 list still show a title. */
const enrichRealestateItemsParentTitles = async (items) => {
  if (!Array.isArray(items) || items.length === 0 || !hasV2Backend()) {
    return items
  }
  const parentIds = [
    ...new Set(
      items
        .map(resolveItemParentKey)
        .filter((id) => id && id !== "undefined" && id !== "null")
    )
  ]
  if (parentIds.length === 0) {
    return items
  }

  const titleByParent = {}
  const boundedParentIds = parentIds.slice(0, 40)
  await Promise.allSettled(
    boundedParentIds.map(async (id) => {
      const row = await withTimeout(fetchLocalRealestatePartnerById(id), 1200)
      const title = partnerTitleFromApiRow(row)
      if (title) {
        titleByParent[id] = title
      }
    })
  )

  return items.map((item) => {
    const pid = resolveItemParentKey(item)
    const t = titleByParent[pid]
    return t ? { ...item, parentCompanyTitle: t } : item
  })
}

const LOCAL_REALESTATE_COMPANIES_KEY = "local_realestate_companies"

const readLocalRealestateCompanies = () => {
  if (typeof window === "undefined") return []
  try {
    const raw = localStorage.getItem(LOCAL_REALESTATE_COMPANIES_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch (_error) {
    return []
  }
}

const writeLocalRealestateCompanies = (items) => {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem(LOCAL_REALESTATE_COMPANIES_KEY, JSON.stringify(items))
  } catch (_error) {
    // ignore local storage failures
  }
}

/** initla state for auth store  */
const initialState = {
  data: [],
  items: [],
  count_new:0,
  count_pending:0,
  count: "",
  current: {},
  currentItem:{},
  perPageValues: [100, 150, 200, 250, 300],
  itemPerPage: 100,
  subitemPerPage: 100,
  currentPage: 1,
  currentPageItems: 1,
  filters: {
    status: {
      value: "ALL",
      label: "label_all"
    }
  },
  filtersItems: {
    status: {
      value: "ALL",
      label: "label_all"
    },
    parent: {
      value: "ALL",
      label: "label_all"
    }
  },
  message: "",
  keyword: null,
  keywordItems: null,
  action: globalConst.action.NONE
}

export const realestate = {
  state: initialState,

  reducers: {
    /**
     * Set translate message
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setMessage: (s, payload) => {
      return {
        ...s,
        message: payload
      }
    },
    setCount_new: (s, payload) => {
      return {
        ...s,
        count_new: payload
      }
    },
    setCurrentItem: (s, payload) => {
      return {
        ...s,
        currentItem: payload
      }
    },
    setCount_pending: (s, payload) => {
      return {
        ...s,
        count_pending: payload
      }
    },
    setCurrentPage: (s, payload) => {
      return {
        ...s,
        currentPage: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setCurrentPageSub: (s, payload) => {
      return {
        ...s,
        currentPageItems: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setCount: (s, payload) => {
      return {
        ...s,
        count: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setData: (s, payload) => {
      return {
        ...s,
        data: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setItems: (s, payload) => {
      return {
        ...s,
        items: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    updateItem: (s, payload) => {
      const index = s.items.findIndex((e) => e.pk === payload.pk)
      const item = s.items[index]
      item.images = item.images.filter((e) => e !== payload.image)
      const _data = s.items.filter((e) => e.pk !== payload.pk)
      _data[index] = item

      return {
        ...s,
        items: _data
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setSales: (s, payload) => {
      return {
        ...s,
        sales: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    pushNewData: (s, payload) => {
      return {
        ...s,
        data: [...s.data, payload]
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setFilters: (s, payload) => {
      return {
        ...s,
        filters: { ...s.filters, ...payload }
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    replaceRecord: (s, payload) => {
      const _data = s.data
      const index = s.data.findIndex((e) => e.pk === payload.pk)
      const item = s.data.find((e) => e.pk === payload.pk)
      item.status = payload.status
      _data[index] = item

      return {
        ...s,
        data: _data
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setKeyword: (s, payload) => {
      return {
        ...s,
        keyword: payload
      }
    },
    setkeywordItems: (s, payload) => {
      return {
        ...s,
        keywordItems: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setitemPerPage: (s, payload) => {
      return {
        ...s,
        itemPerPage: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setitemSubPerPage: (s, payload) => {
      return {
        ...s,
        subitemPerPage: payload
      }
    },
    // setitemSubPerPage
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setCurrent: (s, payload) => {
      return {
        ...s,
        current: payload
      }
    },

    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setAction: (s, payload) => {
      return {
        ...s,
        action: payload
      }
    },
    removeItemFromStore: (s, pk) => {
      return {
        ...s,
        items: s.items.filter(item => item.pk !== pk)
      }
    }
  },
  selectors: (slice, createSelector) => ({
    /**
     *
     * @returns
     */
    error() {
      return createSelector(
        [slice, (rootState) => rootState.loading.models.realestate],
        (realestate, realestateLoading) => {
          return realestateLoading.error || ""
        }
      )
    },
    current() {
      return createSelector([slice], (realestate) => realestate.current)
    },
    currentItem() {
      return createSelector([slice], (realestate) => realestate.currentItem)
    },

    /**
     * success selector used in different
     * auth actions
     *
     * @returns {String}
     */
    success() {
      return createSelector(
        [slice, (rootState) => rootState.loading.models.realestate],
        (realesatate, realesatateLoading) => realesatateLoading.success
      )
    },
    countNew() {
      return createSelector([slice], (realestate) => {
        return realestate.count_new
      })
    },
    countPending() {
      return createSelector([slice], (realestate) => {
        return realestate.count_pending
      })
    },
    /**
     *
     * @returns
     */
    paginationData() {
      return createSelector([slice], (realestate) => {
        let _data = realestate.data
        if (!isEmpty(realestate.filters)) {
          const status = realestate.filters.status
          _data = !!status
            ? status.value !== "ALL"
              ? _data.filter((e) => {
                  const itemStatus =
                    typeof e.status === "object" ? e.status.value : e.status
                  return itemStatus === status.value
                })
              : _data
            : _data
        }
        if (!!realestate.keyword) {
          const options = {
            keys: ["phone", "whatsapp", "title", "titleAr"]
          }
          const fuse = new Fuse(realestate.data, options)
          _data = fuse.search(realestate.keyword)
          _data = _data.map((e) => e.item)
        }
        const offset = (realestate.currentPage - 1) * realestate.itemPerPage

        _data = drop(_data, offset).slice(0, realestate.itemPerPage)
        return _data
      })
    },
    /**
     *
     * @returns
     */
    paginationDataItems() {
      return createSelector([slice], (realestate) => {
        let _data = realestate.items

        if (!isEmpty(realestate.filtersItems)) {
          const status = realestate.filtersItems.status
          _data =
            status && status.value !== "ALL"
              ? _data.filter((e) => {
                  const itemStatus =
                    typeof e.status === "object" ? e.status.value : e.status
                  return itemStatus === status.value
                })
              : _data

          const parent = realestate.filtersItems.parent
          _data =
            parent && parent.value !== "ALL"
              ? _data.filter((e) => resolveItemParentKey(e) === parent.value)
              : _data
        }

        if (!!realestate.keywordItems) {
          const options = {
            keys: ["phone", "whatsapp", "title", "titleAr"]
          }
          const fuse = new Fuse(realestate.items, options)
          _data = fuse.search(realestate.keywordItems)
          _data = _data.map((e) => e.item)
        }

        const offset =
          (realestate.currentPageItems - 1) * realestate.subitemPerPage
        _data = drop(_data, offset).slice(0, realestate.subitemPerPage)
        return _data
      })
    },
  }),
  effects: (dispatch) => ({
    // FETCH_ALL_RELESTATE_ITEMS
    async fetchRealestateItems(ack) {
      appLogger.info("fetchREalestateItems", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.FETCH_ALL_RELESTATE_ITEMS)
      if (shouldSkipProtectedDashboardCalls()) {
        try {
          const items = await getAdminsApiDirect(`/realestate-items/v1?ack=${ack}`)
          const list = await enrichRealestateItemsParentTitles(
            Array.isArray(items) ? items : []
          )
          this.setItems(list)
          this.setAction(globalConst.action.NONE)
          return
        } catch (_error) {
          this.setItems([])
          this.setAction(globalConst.action.NONE)
          return
        }
      }
      try {
        const items = await getAdminsApiDirect(
          `/realestate-items/v1?ack=${ack}`
        )
        this.setItems(items)

        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setItems([])
        this.setAction(globalConst.action.NONE)
        return
      }
    },
    async countNewRealestateItems() {
      appLogger.info("countNewRealestateItems", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.COUNT_NEW_REALESTATE_ITEMS)
      try {
        const items = await getAdminsApiDirect(`/realestate-items/v1/count?ack=new`)
        this.setCount_new(Number(items) || 0)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        if (handleApiAuthError(error)) return
        appLogger.error("countNewRealestateItems", error)
        this.setCount_new(0)
        this.setAction(globalConst.action.NONE)
      }
    },

    async countPendingRealestateItems() {
      appLogger.info("countPendingRealestateItems", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.COUNT_PENDING_REALESTATE_ITEMS)
      try {
        const items = await getAdminsApiDirect(`/realestate-items/v1/count?ack=pending`)
        this.setCount_pending(Number(items) || 0)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        if (handleApiAuthError(error)) return
        appLogger.error("countPendingRealestateItems", error)
        this.setCount_pending(0)
        this.setAction(globalConst.action.NONE)
      }
    },

    /** List real estate companies (`GET /realestate/v1`). */
    async fetchCompanies() {
      appLogger.info("fetchCompanies", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.FETCH_REALESTATE_COMPANY)
      try {
        const items = hasV2Backend()
          ? await v2Request("/realestate/v1")
          : await getAdminsApiDirect("/realestate/v1")
        this.setData(Array.isArray(items) ? items : [])
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        if (handleApiAuthError(error)) return
        appLogger.error("fetchCompanies", error)
        this.setData([])
        this.setAction(globalConst.action.NONE)
      }
    },

    /**
     *
     */
    async saveComapnyRealestate(payload) {
      appLogger.info("saveComapnyRealestate", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.SAVE_REALESTATE_COMPANY)
      if (shouldSkipProtectedDashboardCalls()) {
        if (hasV2Backend()) {
          try {
            await v2Request("/realestate/v1", {
              method: "POST",
              body: payload
            })
            const items = await v2Request("/realestate/v1")
            this.setData(Array.isArray(items) ? items : [])
            this.setMessage("msg_realestate_company_added_sucess")
            this.setAction(globalConst.action.NONE)
            return
          } catch (_error) {
            // fallback to local storage for offline/local failures
          }
        }
        const now = new Date().toISOString()
        const localRecord = {
          ...payload,
          pk: `local-realestate-${Date.now()}`,
          status: "CREATED",
          createdAt: now
        }
        const next = [localRecord, ...readLocalRealestateCompanies()]
        writeLocalRealestateCompanies(next)
        this.setData(next)
        this.setMessage("msg_realestate_company_added_sucess")
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
       await API.post(
          "adminsApi",
          `/realestate/v1/`,
          {
            body: payload
          }
        )
    
        this.setMessage("msg_realestate_company_added_sucess")
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setAction(globalConst.action.NONE)
        return
      }
    },
    /**
     *
     * @param {*} payload
     */
    async updateComapnyRealestate(payload) {
      appLogger.info("updateComapnyRealestate", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.UPDATE_REALESTATE_COMPANY)
      if (shouldSkipProtectedDashboardCalls()) {
        if (hasV2Backend() && payload?.pk) {
          try {
            await v2Request(`/realestate/v1/${payload.pk}`, {
              method: "PUT",
              body: payload
            })
            const items = await v2Request("/realestate/v1")
            this.setData(Array.isArray(items) ? items : [])
            this.setMessage("msg_realestate_company_updated_sucess")
            this.setAction(globalConst.action.NONE)
            return
          } catch (_error) {
            // fallback to local storage for offline/local failures
          }
        }
        const current = readLocalRealestateCompanies()
        const next = current.map((item) =>
          item.pk === payload.pk ? { ...item, ...payload, updatedAt: new Date().toISOString() } : item
        )
        writeLocalRealestateCompanies(next)
        this.setData(next)
        this.setMessage("msg_realestate_company_updated_sucess")
        this.setAction(globalConst.action.NONE)
        return
      }
      try {

        await API.put(
          "adminsApi",
          `/realestate/v1/${payload.pk}`,
          {
            body: payload
          }
        )

        this.setMessage("msg_realestate_company_updated_sucess")
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setAction(globalConst.action.NONE)
        return
      }
    },
    /**
     *
     * @param {*} pk
     */
    async getRealestateCompany(pk) {
      appLogger.info("getRealestateCompany", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.GET_SINGLE_REALESTATE_COMPANY)
      if (shouldSkipProtectedDashboardCalls()) {
        if (hasV2Backend() && pk) {
          const row = await fetchLocalRealestatePartnerById(pk)
          if (row) {
            this.setCurrent(row || {})
            this.setAction(globalConst.action.NONE)
            return
          }
        }
        const row = readLocalRealestateCompanies().find((item) => item.pk === pk) || {}
        this.setCurrent(row)
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        const item = await API.get("adminsApi", `/realestate/v1/${pk}`)
        this.setCurrent(item)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setCurrent({})
        this.setAction(globalConst.action.NONE)
        return
      }
    },

    /**
     *
     * @param {*} pk
     */
    async fetchItems() {
      appLogger.info("fetchItems", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.GET_REALESTATE_ITEMS)
      if (shouldSkipProtectedDashboardCalls()) {
        try {
          const items = await getAdminsApiDirect(`/realestate-items/v1/`)
          const list = await enrichRealestateItemsParentTitles(
            Array.isArray(items) ? items : []
          )
          this.setItems(list)
          this.setAction(globalConst.action.NONE)
          return
        } catch (_error) {
          this.setItems([])
          this.setAction(globalConst.action.NONE)
          return
        }
      }
      try {
        const items = await getAdminsApiDirect(
          `/realestate-items/v1/`
        )
        this.setItems(items)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setItems([])
        this.setAction(globalConst.action.NONE)
        return
      }
    },
    async fetchRealestateItem(pk) {
      appLogger.info("fetchRealestateItem", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.GET_REALESTATE_ITEM)
      if (shouldSkipProtectedDashboardCalls()) {
        try {
          const result = await getAdminsApiDirect(`/v1/items/${pk}`)
          this.setCurrentItem(result || {})
          this.setAction(globalConst.action.NONE)
          return
        } catch (_error) {
          this.setCurrentItem({})
          this.setAction(globalConst.action.NONE)
          return
        }
      }
      try {
        const result=await API.get(
          "adminsApi",
          `/v1/items/${pk}`,
          {}
        )
        this.setCurrentItem(result)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setCurrentItem({})
        this.setAction(globalConst.action.NONE)
        return
      }
    },
    // PUBLISH_REALESTATE_ITEM
    async publishRealestateItem(pk) {
      appLogger.info("publishRealestateItem", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.PUBLISH_REALESTATE_ITEM)
      if (shouldSkipProtectedDashboardCalls()) {
        try {
          await putAdminsApiDirect(`/v1/items/${pk}/publish`, {})
          this.setMessage("Item published successfully")
          this.setAction(globalConst.action.NONE)
          return
        } catch (_error) {
          // Fall through to existing adminsApi call below.
        }
      }
      try {
        await API.put(
          "adminsApi",
          `/v1/items/${pk}/publish`,
          {}
        )
        this.setMessage("Item published successfully")
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setAction(globalConst.action.NONE)
        throw new Error(error)
      }
    },
    async unpublishRealestateItem(pk) {
      appLogger.info("unpublishRealestateItem", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.UNPUBLISH_REALESTATE_ITEM)
      try {
    await API.put(
          "adminsApi",
          `/v1/items/${pk}/unpublish`,
          {}
        )
      this.setMessage("Item unpublished successfully")
      } catch (error) {
        throw new Error(error)
      }
    },

    // GET_REALESTATE_ITEM


    async changeItemStatus(payload) {
      appLogger.info("changeItemStatus", `[REDUX][${STORES.REALESTATE}]`);
      this.setAction(globalConst.action.CHANGE_REALESTATE_ITEM_STATUS);
      if (shouldSkipProtectedDashboardCalls()) {
        try {
          await putAdminsApiDirect(
            `/v1/items/${payload.pk}/status`,
            { ...payload }
          )
          const msg = payload.status === "APPROVED" ? "approved" : "rejected"
          this.setMessage(`Item ${msg} successfully`)
          this.setAction(globalConst.action.NONE)
          return
        } catch (_error) {
          // Fall through to existing adminsApi call below.
        }
      }
      try {


        await API.put(
          "adminsApi",
          `/v1/items/${payload.pk}/status`,
          {
              body:{...payload}    
          }
        );
      const msg=payload.status==="APPROVED"?"approved":"rejected"

        this.setMessage(`Item ${msg} successfully`)
        this.setAction(globalConst.action.NONE)

      } catch (error) {
        this.setAction(globalConst.action.NONE)
        throw new Error(error);
      }
    },

    async updateRealestateItem(payload) {
      appLogger.info("updateRealestateItem", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.UPDATE_REALESTATE_ITEM)
      try {
     await API.put(
          "adminsApi",
          `/v1/items/${payload.pk}`,
          {
            body:{...payload}
          }
        )
        this.setMessage("msg_property_updated_success")
      } catch (error) {
        throw new Error(error)
      }
    },
    async deleteRealestateItem(payload) {
      try {
        appLogger.info("deleteRealestateItem", `[REDUX][${STORES.REALESTATE}]`)
        this.setAction(globalConst.action.DELETE_REALESTATE_ITEM)

        const pk = payload?.pk
        if (!pk) {
          this.setAction(globalConst.action.NONE)
          return
        }

        const path = `/v1/items/${pk}`
        if (hasV2Backend()) {
          await v2RequestWithCommonsFallback(path, { method: "DELETE" })
        } else {
          await API.del("adminsApi", path, {})
        }

        this.removeItemFromStore(pk)
        this.setMessage("msg_item_deleted")
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setAction(globalConst.action.NONE)
        throw new Error(error)
      }
    },
    async activateRealestate(payload) {
      appLogger.info("activateRealestate", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.ACTIVATE_REALESTATE)

      const pk = payload?.pk
      if (!pk) {
        this.setAction(globalConst.action.NONE)
        return
      }

      if (shouldSkipProtectedDashboardCalls()) {
        if (hasV2Backend()) {
          try {
            await v2Request(`/realestate/v1/${pk}/activate`, {
              method: "PUT",
              body: omit(payload, ["pk"])
            })
            const items = await v2Request("/realestate/v1")
            this.setData(Array.isArray(items) ? items : [])
            this.setMessage("msg_company_activated_success")
            this.setAction(globalConst.action.NONE)
            return
          } catch (_error) {
            // fallback to local storage for offline/local failures
          }
        }
        const next = readLocalRealestateCompanies().map((item) =>
          item.pk === pk
            ? { ...item, status: "ACTIVE", updatedAt: new Date().toISOString() }
            : item
        )
        writeLocalRealestateCompanies(next)
        this.setData(next)
        this.setMessage("msg_company_activated_success")
        this.setAction(globalConst.action.NONE)
        return
      }

      try {
        await API.put("adminsApi", `/realestate/v1/${pk}/activate`, {
          body: omit(payload, ["pk"])
        })
        this.setMessage("msg_company_activated_success")
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setAction(globalConst.action.NONE)
        throw new Error(error)
      }
    },

       async resetRealestate(payload) {
          appLogger.info("resetRealestate", `[REDUX][${STORES.REALESTATE}]`)
          this.setAction(globalConst.action.RESET_REALESTATE)
          if (shouldSkipProtectedDashboardCalls()) {
            if (hasV2Backend() && payload?.pk) {
              try {
                await v2Request(`/realestate/v1/${payload.pk}/reset`, {
                  method: "POST",
                  body: omit(payload, ["pk"])
                })
                this.setMessage("msg_company_reset_success")
                this.setAction(globalConst.action.NONE)
                return
              } catch (_error) {
                // fallback to local success message for offline/local failures
              }
            }
            this.setMessage("msg_company_reset_success")
            this.setAction(globalConst.action.NONE)
            return
          }
          try {
    
             await API.put(
              "adminsApi",
              `/realestate/v1/${payload.pk}/reset`,
              {
                body:omit(payload, ["pk"])
              }
            )
    
            this.setMessage("msg_company_reset_success")
            this.setAction(globalConst.action.NONE)
    
          } catch (error) {
            this.setAction(globalConst.action.NONE)
            return
          }
        },
    /**
     *
     * @param {*} param0
     */
    async addImage({ pk, createdAt, image, images }) {
      appLogger.info("addImage", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.ADD_PHOTO_REALESTATE_ITEM)

      const _images = images
      _images.push(image)
      try {
        await API.put("realestateApiGateway", `/aqar/${pk}`, {
          body: { images: _images, createdAt }
        })
      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     *
     * @param {*} param0
     */
    async updateCover({ pk, createdAt, cover }) {
      appLogger.info("addImage", `[REDUX][${STORES.REALESTATE}]`)

      try {
        await API.put("realestateApiGateway", `/aqar/${pk}`, {
          body: { cover, createdAt }
        })
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     * @param {*} param0
     */
    async deleteImage({ pk, image, createdAt, images }) {
      appLogger.info("deleteImage", `[REDUX][${STORES.REALESTATE}]`)

      try {
        await API.post("realestateApiGateway", `/aqar/${pk}`, {
          body: { createdAt, images }
        })
        this.updateItem({ pk, image })
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     * @param {*} payload
     */
    async saveItem(payload) {
      appLogger.info("saveItem", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.SAVE_REALESTATE_ITEM)
      try {
      
       await API.post(
          "adminsApi",
          `/v1/items`,
          {
            body:{...payload,}
          }
        )

        this.setMessage("msg_item_saved_successfully")
      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     *
     * @param {*} pk
     */
    async getItem(pk) {
      appLogger.info("getItem", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.GET_REALESTATE_ITEM)
      try {
        const result = await API.get("realestateApiGateway", `/aqar/${pk}`)

        this.setCurrent(result)
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     * @param {*} pk
     */
    async deleteAqar({ pk, createdAt }) {
      appLogger.info("deleteAqar", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.DELETE_REALESTATE_ITEM)
      try {
        await API.post("realestateApiGateway", `/aqar/${pk}`, {
          body: { createdAt }
        })
        this.setMessage("msg_item_deleted")
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     * @param {*} pk
     * @param {*} rate
     */
    async disableCompanyRealestate({ pk }) {
      appLogger.info("disableCompanyRealestate", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.DISABLE_REALESTATE_COMPANY)
      if (shouldSkipProtectedDashboardCalls()) {
        if (hasV2Backend() && pk) {
          try {
            await v2Request(`/realestate/v1/${pk}/disable`, { method: "PUT", body: {} })
            const items = await v2Request("/realestate/v1")
            this.setData(Array.isArray(items) ? items : [])
            this.setMessage("msg_disable_company_success")
            this.setAction(globalConst.action.NONE)
            return
          } catch (_error) {
            // fallback to local storage for offline/local failures
          }
        }
        const next = readLocalRealestateCompanies().map((item) =>
          item.pk === pk ? { ...item, status: "DISABLED", updatedAt: new Date().toISOString() } : item
        )
        writeLocalRealestateCompanies(next)
        this.setData(next)
        this.setMessage("msg_disable_company_success")
        this.setAction(globalConst.action.NONE)
        return
      }
      try {


        await API.put(
          "adminsApi",
          `/realestate/v1/${pk}/disable`,
          {
            body:{}
          }
        )
        this.setMessage("msg_disable_company_success")
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setAction(globalConst.action.NONE)
        return
      }
    },

    async enableCompanyRealestate({ pk }) {
      appLogger.info("enableCompanyRealestate", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.ENABLE_REALESTATE_COMPANY)
      if (shouldSkipProtectedDashboardCalls()) {
        if (hasV2Backend() && pk) {
          try {
            await v2Request(`/realestate/v1/${pk}/enable`, { method: "PUT", body: {} })
            const items = await v2Request("/realestate/v1")
            this.setData(Array.isArray(items) ? items : [])
            this.setMessage("msg_enable_company_success")
            this.setAction(globalConst.action.NONE)
            return
          } catch (_error) {
            // fallback to local storage for offline/local failures
          }
        }
        const next = readLocalRealestateCompanies().map((item) =>
          item.pk === pk ? { ...item, status: "ACTIVE", updatedAt: new Date().toISOString() } : item
        )
        writeLocalRealestateCompanies(next)
        this.setData(next)
        this.setMessage("msg_enable_company_success")
        this.setAction(globalConst.action.NONE)
        return
      }
      try {

        await API.put(
          "adminsApi",
          `/realestate/v1/${pk}/enable`,
          {
            body:{}
          }
        )
        this.setMessage("msg_enable_company_success")
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setAction(globalConst.action.NONE)
        return
      }
    },

    async deleteCompanyRealestate({ pk }) {
      appLogger.info("deleteCompanyRealestate", `[REDUX][${STORES.REALESTATE}]`)
      this.setAction(globalConst.action.DELETE_REALESTATE_COMPANY)
      if (shouldSkipProtectedDashboardCalls()) {
        if (hasV2Backend() && pk) {
          try {
            await v2Request(`/realestate/v1/${pk}`, { method: "DELETE" })
            const items = await v2Request("/realestate/v1")
            this.setData(Array.isArray(items) ? items : [])
            this.setMessage("msg_company_deleted_success")
            this.setAction(globalConst.action.NONE)
            return
          } catch (_error) {
            // fallback to local storage for offline/local failures
          }
        }
        const next = readLocalRealestateCompanies().filter((item) => item.pk !== pk)
        writeLocalRealestateCompanies(next)
        this.setData(next)
        this.setMessage("msg_company_deleted_success")
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        await API.del(
          "adminsApi",
          `/realestate/v1/${pk}`,
          {}
        )
        this.setMessage("msg_company_deleted_success")
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setAction(globalConst.action.NONE)
        return
      }
    },

    /**
     *
     * @param {*} payload
     */
    async updateAgence(payload) {
      appLogger.info("updateAgence", `[REDUX][${STORES.EXHIBITIONS}]`)
      this.setAction(globalConst.action.UPDATE_AGENCES)
      try {
        await API.put("exhibitionsApiGateway", `/parents/${payload.pk}`)
        this.setMessage("msg_agency_updated_sucess")
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     */
    async fetchSales() {
      appLogger.info("fetchSales", `[REDUX][${STORES.BIDDING}]`)
      this.setAction(globalConst.action.FETCH_SALES)
      try {
        const { items, count } = await API.get("biddingApiGateway", "/sales")

        // this.setData(items)
        // this.setCount(count)
        // this.setAction(globalConst.action.NONE)
      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     *
     */
    onAcionReset() {
      this.setAction(globalConst.action.NONE)
    },
    /**
     *
     * @param {*} param0
     */
    resetCurrentRecord(payload = {}) {
      this.setCurrent(payload)
    },
    resetCurrentItem(payload = {}) {
      this.setCurrentItem(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onItemPerPageChange(payload) {
      this.setitemPerPage(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onSubItemPerPageChange(payload) {
      this.setitemSubPerPage(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onFiltersChange(payload) {
      this.setFilters(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onCurrentPageChange(payload) {
      this.setCurrentPage(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onCurrentPageSubChange(payload) {
      this.setCurrentPageSub(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onKeywordChange(payload) {
      this.setKeyword(payload)
    },
    onKeywordItemsChange(payload) {
      this.setkeywordItems(payload)
    }
  })
}
